import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchCustomerRecurringOrders } from '@/supabase-client'
import { RefreshCw, Plus } from 'lucide-react'
import CreateRecurringOrderDialog from './CreateRecurringOrderDialog'
import EditRecurringOrderDialog from '../EditRecurringOrderDialog'

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function RecurringOrdersSection({ customerId }) {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)

  const loadOrders = async () => {
    if (!customerId) return

    try {
      setLoading(true)
      const data = await fetchCustomerRecurringOrders(customerId)
      setOrders(data)
    } catch (error) {
      console.error('Error loading recurring orders:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
  }, [customerId])

  const handleOrderCreated = () => {
    loadOrders()
  }

  const handleOrderClick = (order) => {
    // Transform order for edit dialog
    setSelectedOrder({
      id: order.id,
      days_of_week: order.days_of_week,
      end_type: order.end_type,
      end_date: order.end_date,
      max_occurrences: order.max_occurrences,
      items: order.recurring_order_items?.map(item => ({
        product_id: item.product_id,
        product_type_id: item.product_type_id,
        quantity: item.quantity
      })) || []
    })
    setEditDialogOpen(true)
  }

  const handleOrderSaved = () => {
    loadOrders()
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Recurring Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  // Empty state when no recurring orders
  if (!orders || orders.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Recurring Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 px-4 rounded-lg border border-dashed border-border bg-muted/20">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground mb-1">
              No recurring orders set up
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Recurring orders automatically generate sales on a schedule
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Recurring Order
            </Button>
          </div>
        </CardContent>

        <CreateRecurringOrderDialog
          customerId={customerId}
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreated={handleOrderCreated}
        />
      </Card>
    )
  }

  const formatDaysOfWeek = (daysArray) => {
    if (!daysArray || daysArray.length === 0) return 'No days set'
    return daysArray
      .sort((a, b) => a - b)
      .map(day => DAYS_OF_WEEK[day])
      .join(', ')
  }

  const formatProducts = (orderItems) => {
    if (!orderItems || orderItems.length === 0) return 'No products'
    return orderItems
      .map(item => `${item.quantity}kg ${item.products?.strain || 'Unknown'}`)
      .join(', ')
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Recurring Orders ({orders.length})
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {orders.map((order) => (
          <div
            key={order.id}
            onClick={() => handleOrderClick(order)}
            className="p-3 rounded-lg border border-border bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-medium text-sm">
                  {order.name || 'Unnamed Order'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDaysOfWeek(order.days_of_week)}
                </p>
              </div>
              <Badge variant={order.active ? 'default' : 'secondary'}>
                {order.active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            <p className="text-sm text-foreground mb-2">
              {formatProducts(order.recurring_order_items)}
            </p>

            {order.next_delivery_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Next delivery:</span>
                <span className="font-medium text-foreground">
                  {new Date(order.next_delivery_date).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}

            {order.paused_until && new Date(order.paused_until) > new Date() && (
              <div className="mt-2 pt-2 border-t">
                <Badge variant="outline" className="text-xs">
                  Paused until {new Date(order.paused_until).toLocaleDateString('en-GB')}
                </Badge>
              </div>
            )}
          </div>
        ))}
      </CardContent>

      <CreateRecurringOrderDialog
        customerId={customerId}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleOrderCreated}
      />

      {selectedOrder && (
        <EditRecurringOrderDialog
          order={selectedOrder}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSave={handleOrderSaved}
        />
      )}
    </Card>
  )
}
